import styled, { css } from 'styled-components';
import { Theme } from '../theme';
import { Colors, colors } from '../utils/colors';

import { Label as AriaLabel, FieldError } from 'react-aria-components';

export type TextProps = {
    centered?: boolean;
    color?: Colors;
    fw?: 'bold' | '500' | 'normal';
};

const commonStyles = css<TextProps>`
    text-align: ${(props): string => (props.centered ? 'center' : 'left')};
    color: ${(props): string =>
        props.color ? colors[props.color] : 'inherit'};
    line-height: 1.25;
    margin: 0;
    max-width: 70ch;
    word-wrap: break-word;
`;

export const BodyStyle = css<TextProps>`
    ${commonStyles}
    font-weight: ${(props): string => (props.fw ? props.fw : 'normal')};
    font-size: 16px;
    line-height: 20px;
    white-space: pre-wrap;
`;

export const BodyText = styled.p<TextProps>`
    ${BodyStyle}
`;

export const Label = styled(AriaLabel)<TextProps>`
    ${BodyStyle}
    display: inline-block;
    color: ${colors.gray900};
    font-weight: bold;
    margin: 0 0 0 0;
    font-size: 18px;
`;

const H3Style = css<TextProps>`
    ${commonStyles}
    font-weight: ${(props): string => (props.fw ? props.fw : 'bold')};
    font-size: 24px;
    line-height: 28px;
`;

export const H3 = styled.h3<TextProps>`
    ${H3Style}
`;

type TextStyle = React.CSSProperties;

type Mobile = { mobile: TextStyle };

export interface DefaultTextProps {
    error: { default: TextStyle };
    links: {
        focus: TextStyle;
        default: TextStyle;
        active: TextStyle;
        hover: TextStyle;
        visited: {
            focus: TextStyle;
            default: TextStyle;
            active: TextStyle;
            hover: TextStyle;
        };
    };
    default: TextStyle;
    typography: {
        defaults: {
            color: string;
            weights: {
                regular: TextStyle;
                medium: TextStyle;
                bold: TextStyle;
            };
        };

        body: TextStyle & Mobile;
        body2: TextStyle & Mobile;
        button: TextStyle;
        label: TextStyle;
        h3: TextStyle & Mobile;
        h4: TextStyle & Mobile;
    };
}

export const defaultTextProps: DefaultTextProps = {
    links: {
        default: {
            color: colors.blue700,
            textDecoration: 'underline',
        },
        visited: {
            default: {
                color: colors.pink800,
                textDecoration: 'underline',
            },
            active: {
                color: colors.blue900,
                textDecoration: 'underline',
            },
            hover: {
                textDecoration: 'unset',
            },
            focus: {
                outline: `4px solid ${colors.indigo400}`,
            },
        },
        active: {
            color: colors.blue900,
            textDecoration: 'underline',
            outline: 'unset',
        },
        hover: {
            textDecoration: 'unset',
        },
        focus: {
            borderRadius: '0.25rem',
            outline: `4px solid ${colors.indigo400}`,
        },
    },
    error: {
        default: {
            color: colors.red700,
        },
    },
    default: {},
    typography: {
        defaults: {
            color: colors.gray900,
            weights: {
                medium: {
                    fontWeight: 500,
                },
                bold: {
                    fontWeight: 700,
                },
                regular: {
                    fontWeight: 400,
                },
            },
        },
        button: {
            color: colors.blue900,
            fontSize: '1rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
        },
        label: {
            fontSize: '1rem',
            lineHeight: '1.25rem',
            fontWeight: 700,
        },
        body: {
            mobile: {
                fontSize: '1rem',
                lineHeight: '1.25rem',
            },

            fontSize: '1.125rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
        },
        body2: {
            fontWeight: 400,
            lineHeight: '1.25rem',
            fontSize: '1rem',

            mobile: {
                lineHeight: '1.125rem',
                fontSize: '0.875rem',
            },
        },
        h3: {
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: '2.4rem',
            mobile: {
                fontSize: '1.5rem',
                lineHeight: '1.6rem',
            },
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 500,
            lineHeight: '1.875rem',
            mobile: { fontSize: '1.25rem', lineHeight: '1.625rem' },
        },
    },
};

const getElementType = (variant: keyof typeof defaultTextProps.typography) => {
    if (variant === 'h3' || variant === 'h4') return variant;
    if (variant === 'label') return AriaLabel;
    return 'p';
};

interface TextPropsNew {
    color?: string;
    variant: keyof typeof defaultTextProps.typography;
    weight?: keyof (typeof defaultTextProps.typography.defaults)['weights'];
    fonts?: keyof typeof defaultTextProps.typography;
    align?: 'left' | 'center' | 'right';
}

export const mobileTextStyles = (
    variant: keyof Theme['texts']['typography'],
) => css`
    @media (max-width: 40em) {
        font-size: ${(props) =>
            props.theme.texts.typography[variant]?.mobile?.fontSize ||
            props.theme.texts.typography[variant]?.fontSize};
        line-height: ${(props) =>
            props.theme.texts.typography[variant]?.mobile?.lineHeight ||
            props.theme.texts.typography[variant]?.lineHeight};
    }
`;

export const textStyles = ({
    weight,
    variant,
    align,
}: {
    variant: keyof Theme['texts']['typography'];
    weight?: keyof (typeof defaultTextProps.typography.defaults)['weights'];
    align?: 'left' | 'center' | 'right';
}) => css`
    color: ${(props) => props.theme.texts.typography.defaults.color};
    ${(props) => props.theme.texts.typography[variant]}
    ${(props) =>
        weight && props.theme.texts.typography.defaults.weights[weight]}
    background-color: inherit;
    text-align: ${align || 'left'};
    ${mobileTextStyles(variant)}
`;

const StyledTextComp = styled.p<TextPropsNew>`
    ${({ variant, weight }) => textStyles({ variant, weight })}
    margin: 0px;
    padding: 0px;
    text-align: ${({ align }) => align || 'left'};
    color: ${({ color }) => {
        return color || 'inherit';
    }};
`;

export const StyledText: React.FC<
    TextPropsNew & {
        as?: React.ElementType;
        children: React.ReactNode;
    } & React.HTMLProps<HTMLElement>
> = ({ as, variant, weight, ...props }) => {
    const Component = as || getElementType(variant) || 'p';
    return (
        <StyledTextComp
            as={Component}
            variant={variant}
            weight={weight}
            {...props}
        />
    );
};

export const ErrorText = styled(StyledText).attrs({
    'aria-live': 'polite',
    'aria-relevant': 'additions removals',
})`
    ${(props) => props.theme.texts.error.default}
`;

export const FieldErrorText = styled(FieldError)`
    ${(props) => props.theme.texts.error.default}
`;

export const linkStyles = css`
    ${(props) => props.theme.texts.default}
    ${(props) => props.theme.texts.links.default}

    &:hover {
        ${(props) => props.theme.texts.links.hover}
    }

    &:focus {
        ${(props) => props.theme.texts.links.focus}
    }

    &:active {
        ${(props) => props.theme.texts.links.active}
    }

    &:visited {
        ${(props) => props.theme.texts.links.visited.default};

        &:hover {
            ${(props) => props.theme.texts.links.visited.hover}
        }

        &:focus {
            ${(props) => props.theme.texts.links.visited.focus}
        }

        &:active {
            ${(props) => props.theme.texts.links.visited.active}
        }
    }
`;

export const LinkText = styled(StyledText).attrs({
    as: 'a',
    rel: 'noopener',
    target: '_blank',
})`
    ${linkStyles}
` as React.ComponentType<
    React.ComponentProps<typeof StyledText> &
        Omit<React.ComponentPropsWithoutRef<'a'>, 'as'> & { href: string }
>;

StyledText.displayName = 'Text';
